import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { BRAND_COLORS } from '../styles/theme';
import { useAuthV2 } from '../contexts/AuthContextV2';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useTranslation, LanguageSwitcher } from '../i18n';
import XERLogo from '../components/XERLogo';

type Step = 'email' | 'otp' | 'password' | 'success';

export const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { resetPassword, verifyPasswordResetOtp, updatePassword } = useAuthV2();
  const { showError, showSuccess } = useErrorHandler();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(60);

  // Countdown timer for resend button
  useEffect(() => {
    if (step === 'otp' && !canResend && resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (resendCountdown === 0) {
      setCanResend(true);
    }
  }, [step, canResend, resendCountdown]);

  const validateEmail = (): boolean => {
    const newErrors: string[] = [];

    if (!email.trim()) {
      newErrors.push(t('auth.emailRequired'));
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.push(t('auth.emailInvalid'));
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const validateOtp = (): boolean => {
    const newErrors: string[] = [];

    if (!otp.trim()) {
      newErrors.push(t('passwordReset.codeRequired'));
    } else if (!/^\d{6}$/.test(otp.trim())) {
      newErrors.push(t('passwordReset.codeMustBe6Digits'));
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const validatePassword = (): boolean => {
    const newErrors: string[] = [];

    if (!password) {
      newErrors.push(t('auth.passwordRequired'));
    } else if (password.length < 6) {
      newErrors.push(t('auth.passwordMinLength'));
    }

    if (password !== confirmPassword) {
      newErrors.push(t('auth.passwordsDoNotMatch'));
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSendOtp = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateEmail()) return;

    setLoading(true);
    setErrors([]);

    const { error } = await resetPassword(email);

    if (error) {
      setErrors([error.message]);
      showError(error, t('errors.generic'));
      setLoading(false);
    } else {
      showSuccess(t('passwordReset.codeSent'));
      setStep('otp');
      setCanResend(false);
      setResendCountdown(60);
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    setErrors([]);

    const { error } = await resetPassword(email);

    if (error) {
      setErrors([error.message]);
      showError(error, t('errors.generic'));
      setLoading(false);
    } else {
      showSuccess(t('passwordReset.codeResent'));
      setCanResend(false);
      setResendCountdown(60);
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateOtp()) return;

    setLoading(true);
    setErrors([]);

    const { error } = await verifyPasswordResetOtp(email, otp.trim());

    if (error) {
      setErrors([error.message || t('passwordReset.invalidCode')]);
      showError(error, t('errors.generic'));
      setLoading(false);
    } else {
      showSuccess(t('passwordReset.codeVerified'));
      setStep('password');
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validatePassword()) return;

    setLoading(true);
    setErrors([]);

    const { error } = await updatePassword(password);

    if (error) {
      setErrors([error.message]);
      showError(error, t('errors.generic'));
      setLoading(false);
    } else {
      showSuccess(t('passwordReset.passwordUpdated'));
      setStep('success');
      setLoading(false);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    }
  };

  // Success state
  if (step === 'success') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: 'url(/login-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: 2,
          position: 'relative',
        }}
      >
        {/* Language Switcher - top right */}
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <LanguageSwitcher variant="compact" />
        </Box>

        <Paper
          elevation={24}
          sx={{
            p: 4,
            maxWidth: 500,
            width: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: BRAND_COLORS.forestGreen.main, mb: 1 }}
            >
              {t('passwordReset.successTitle')}
            </Typography>
          </Box>

          <Alert severity="success" sx={{ mb: 3 }}>
            {t('passwordReset.successMessage')}
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            {t('passwordReset.redirecting')}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} sx={{ color: BRAND_COLORS.forestGreen.main }} />
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: 'url(/login-bg.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        padding: 2,
        position: 'relative',
      }}
    >
      {/* Language Switcher - top right */}
      <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
        <LanguageSwitcher variant="compact" />
      </Box>

      <Paper
        elevation={3}
        sx={{
          padding: 4,
          maxWidth: 450,
          width: '100%',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.2)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          backdropFilter: 'blur(8px)',
        }}
      >
        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <XERLogo width={120} height={50} />
          <Typography
            variant="h5"
            sx={{
              mt: 2,
              color: BRAND_COLORS.forestGreen.main,
              fontWeight: 600,
            }}
          >
            {step === 'email' && t('passwordReset.title')}
            {step === 'otp' && t('passwordReset.verifyCode')}
            {step === 'password' && t('passwordReset.setNewPassword')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {step === 'email' && t('passwordReset.emailPrompt')}
            {step === 'otp' && t('passwordReset.codeSentTo', { email })}
            {step === 'password' && t('passwordReset.newPasswordPrompt')}
          </Typography>
        </Box>

        {/* Error Messages */}
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          </Alert>
        )}

        {/* Step 1: Email Input */}
        {step === 'email' && (
          <form onSubmit={handleSendOtp}>
            <TextField
              fullWidth
              label={t('auth.email')}
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              disabled={loading}
              autoComplete="email"
              autoFocus
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 3,
                mb: 2,
                backgroundColor: BRAND_COLORS.forestGreen.main,
                '&:hover': {
                  backgroundColor: '#002B20',
                },
              }}
            >
              {loading ? <CircularProgress size={24} /> : t('passwordReset.sendVerificationCode')}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => navigate('/')}
                disabled={loading}
                sx={{ color: BRAND_COLORS.forestGreen.main }}
              >
                {t('auth.backToLogin')}
              </Link>
            </Box>
          </form>
        )}

        {/* Step 2: OTP Verification */}
        {step === 'otp' && (
          <form onSubmit={handleVerifyOtp}>
            <TextField
              fullWidth
              label={t('passwordReset.verificationCode')}
              name="otp"
              type="text"
              value={otp}
              onChange={(e) => {
                // Only allow numbers and max 6 digits
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setOtp(value);
              }}
              margin="normal"
              disabled={loading}
              autoComplete="one-time-code"
              autoFocus
              inputProps={{
                maxLength: 6,
                pattern: '[0-9]*',
                inputMode: 'numeric',
              }}
              helperText={t('passwordReset.codeHelperText')}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || otp.length !== 6}
              sx={{
                mt: 3,
                mb: 2,
                backgroundColor: BRAND_COLORS.forestGreen.main,
                '&:hover': {
                  backgroundColor: '#002B20',
                },
              }}
            >
              {loading ? <CircularProgress size={24} /> : t('passwordReset.verifyCodeButton')}
            </Button>

            <Box sx={{ textAlign: 'center', mb: 2 }}>
              {canResend ? (
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={handleResendOtp}
                  disabled={loading}
                  sx={{ color: BRAND_COLORS.forestGreen.main }}
                >
                  {t('passwordReset.resendCode')}
                </Link>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('passwordReset.resendCodeIn', { seconds: resendCountdown })}
                </Typography>
              )}
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => {
                  setStep('email');
                  setOtp('');
                  setErrors([]);
                }}
                disabled={loading}
                sx={{ color: BRAND_COLORS.forestGreen.main }}
              >
                {t('passwordReset.changeEmail')}
              </Link>
            </Box>
          </form>
        )}

        {/* Step 3: Password Input */}
        {step === 'password' && (
          <form onSubmit={handleUpdatePassword}>
            <TextField
              fullWidth
              label={t('passwordReset.newPassword')}
              name="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              disabled={loading}
              autoComplete="new-password"
              helperText={t('passwordReset.passwordHelperText')}
              autoFocus
            />

            <TextField
              fullWidth
              label={t('passwordReset.confirmNewPassword')}
              name="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              disabled={loading}
              autoComplete="new-password"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 3,
                mb: 2,
                backgroundColor: BRAND_COLORS.forestGreen.main,
                '&:hover': {
                  backgroundColor: '#002B20',
                },
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />{' '}
                  {t('passwordReset.updatingPassword')}
                </>
              ) : (
                t('passwordReset.resetPasswordButton')
              )}
            </Button>
          </form>
        )}
      </Paper>
    </Box>
  );
};
